import { isFilled } from "lopermut"
import { MetafieldGroup } from "modules/settings/components/integrations/shopify/components/Metafields/MetafieldsKeyList"
import { useEffect, useRef, useState } from "react"
import { ShopifyMetafieldsConfig } from "schema"
import { useShopifyIntegrationQuery } from "./useShopifyIntegrationQuery"
import { useUpdateShopifyMetafieldsConfigMutation } from "./useUpdateShopifyMetafieldsConfigMutation"

const GROUPS: MetafieldGroup[] = [
  "product",
  "productVariant",
  "order",
  "productTags",
  "orderTags",
  "lineItemProperty",
]

const EMPTY_CONFIG: ShopifyMetafieldsConfig = {
  product: [],
  productVariant: [],
  order: [],
  productTags: [],
  orderTags: [],
  lineItemProperty: [],
}

// Empty rows (key & description both empty) are skipped — they are placeholders the user
// just added. Rows with a key but no description are NOT dropped here; they are caught by
// the validity check below so the user sees an error instead of silently losing the row.
function isStartedEntry(entry: { key?: string; description?: string }) {
  return isFilled(entry.key) || isFilled(entry.description)
}

function sanitize(config: ShopifyMetafieldsConfig) {
  return {
    product: config.product.filter(isStartedEntry),
    productVariant: config.productVariant.filter(isStartedEntry),
    order: config.order.filter(isStartedEntry),
    productTags: config.productTags.filter(isStartedEntry),
    orderTags: config.orderTags.filter(isStartedEntry),
    lineItemProperty: config.lineItemProperty.filter(isStartedEntry),
  }
}

function isEntryValid(entry: { key?: string; description?: string }) {
  return isFilled(entry.key) && isFilled(entry.description)
}

function isConfigValid(config: ShopifyMetafieldsConfig) {
  return GROUPS.every((g) => config[g].filter(isStartedEntry).every(isEntryValid))
}

function serialize(config: ShopifyMetafieldsConfig) {
  return JSON.stringify(sanitize(config))
}

export function useMetafieldsConfigState() {
  const { data } = useShopifyIntegrationQuery()
  const { mutateAsync } = useUpdateShopifyMetafieldsConfigMutation()
  const [config, setConfig] = useState<ShopifyMetafieldsConfig>(EMPTY_CONFIG)
  const configRef = useRef<ShopifyMetafieldsConfig>(EMPTY_CONFIG)
  const lastSavedRef = useRef<string>(serialize(EMPTY_CONFIG))

  useEffect(() => {
    configRef.current = config
  }, [config])

  useEffect(() => {
    if (!data?.metafieldsConfig) return
    // Don't clobber in-progress local edits with the server snapshot.
    if (!isConfigValid(configRef.current)) return
    const next = { ...EMPTY_CONFIG, ...data.metafieldsConfig }
    setConfig(next)
    configRef.current = next
    lastSavedRef.current = serialize(next)
  }, [data])

  const addEntry = (group: MetafieldGroup) => {
    setConfig((prev) => {
      const next = { ...prev, [group]: [...prev[group], { key: "", description: "" }] }
      configRef.current = next
      return next
    })
  }

  const autosave = () => {
    if (!isConfigValid(configRef.current)) return
    const sanitized = sanitize(configRef.current)
    const next = serialize(sanitized)
    if (next === lastSavedRef.current) return
    lastSavedRef.current = next
    void mutateAsync(sanitized)
  }

  const setConfigSync: typeof setConfig = (updater) => {
    setConfig((prev) => {
      const next =
        typeof updater === "function"
          ? (updater as (p: ShopifyMetafieldsConfig) => ShopifyMetafieldsConfig)(prev)
          : updater
      configRef.current = next
      return next
    })
  }

  return { config, setConfig: setConfigSync, addEntry, autosave }
}
